/**
 * B2BOrderPrintView
 *
 * A4-sized, RTL Hebrew print template for a single B2B order.
 *
 * Designed to be rendered inside a React ref and passed to react-to-print
 * so the browser's native print-to-PDF engine handles the output.
 *
 * Usage:
 *   const printRef = useRef<HTMLDivElement>(null);
 *   const handlePrint = useReactToPrint({ contentRef: printRef });
 *
 *   <B2BOrderPrintView ref={printRef} data={orderPrintData} />
 *   <button onClick={handlePrint}>הדפס / שמור PDF</button>
 */

import { forwardRef } from 'react';

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface B2BOrderPrintData {
  orderId: string;
  supplierName: string;
  clientName?: string;
  date: string;          // ISO string – rendered as DD/MM/YYYY
  status: string;        // Hebrew label
  lines: {
    product_name: string;
    product_sku: string;
    requested_quantity: number;
    approved_quantity: number;
    unit_price: number;
    total_line_price: number;
    discount?: number;
  }[];
  subtotal: number;
  discountTotal?: number;
  discountReason?: string | null;
  vatRate: number | null;
  isVatLiable: boolean;
  grandTotal: number;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso: string): string => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const fmtCurrency = (n: number): string =>
  `₪${n.toLocaleString('he-IL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// ─── Component ─────────────────────────────────────────────────────────────────

export const B2BOrderPrintView = forwardRef<
  HTMLDivElement,
  { data: B2BOrderPrintData }
>(function B2BOrderPrintView({ data }, ref) {
  const showVat = data.isVatLiable && data.vatRate != null && data.vatRate > 0;
  const vatAmount = showVat
    ? data.grandTotal - data.subtotal
    : 0;
  const hasDiscount = (data.discountTotal ?? 0) > 0;

  return (
    <>
      {/*
        ── Print-only styles injected via a <style> tag so they work regardless
        of Tailwind purging, and survive react-to-print's iframe approach.
      */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Heebo:wght@300;400;500;600;700;800&display=swap');

        .b2b-order-print {
          font-family: 'Heebo', 'Arial Hebrew', 'Arial', sans-serif;
          direction: rtl;
          background: #ffffff;
          color: #111827;
          width: 210mm;
          min-height: 297mm;
          margin: 0 auto;
          padding: 16mm 18mm 24mm 18mm;
          box-sizing: border-box;
          position: relative;
        }

        /* Screen preview card shadow */
        @media screen {
          .b2b-order-print {
            box-shadow: 0 4px 32px rgba(0,0,0,0.12);
            border-radius: 4px;
          }
        }

        @media print {
          @page { size: A4 portrait; margin: 0; }
          .b2b-order-print {
            width: 210mm;
            min-height: 297mm;
            padding: 16mm 18mm 24mm 18mm;
            box-shadow: none;
            border-radius: 0;
          }
          .b2b-no-print { display: none !important; }
        }

        /* ── Header ─────────────────────────────────────────────────── */
        .b2b-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding-bottom: 8mm;
          border-bottom: 2.5px solid #2563eb;
        }
        .b2b-logo img {
          height: 40px;
          width: auto;
        }
        .b2b-doc-title {
          font-size: 17pt;
          font-weight: 700;
          color: #111827;
          margin: 0;
          text-align: left;
        }
        .b2b-doc-subtitle {
          font-size: 8pt;
          color: #6b7280;
          margin: 3px 0 0;
          direction: ltr;
          text-align: left;
        }

        /* ── Meta grid ──────────────────────────────────────────────── */
        .b2b-meta-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 5mm;
          margin: 8mm 0 6mm;
        }
        .b2b-meta-box {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 4mm 5mm;
        }
        .b2b-meta-label {
          font-size: 7pt;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #9ca3af;
          margin-bottom: 2px;
        }
        .b2b-meta-value {
          font-size: 11pt;
          font-weight: 600;
          color: #111827;
        }
        .b2b-status-badge {
          display: inline-block;
          margin-top: 3px;
          padding: 1mm 3mm;
          font-size: 8pt;
          font-weight: 600;
          color: #2563eb;
          background: #eff6ff;
          border-radius: 4px;
        }

        /* ── Items table ────────────────────────────────────────────── */
        .b2b-table {
          width: 100%;
          border-collapse: collapse;
          margin: 4mm 0;
        }
        .b2b-table thead tr {
          border-bottom: 2px solid #2563eb;
        }
        .b2b-table thead th {
          padding: 3mm 3mm;
          font-size: 8.5pt;
          font-weight: 700;
          text-align: right;
          color: #374151;
        }
        .b2b-table thead th.b2b-num {
          text-align: center;
        }
        .b2b-table thead th.b2b-left {
          text-align: left;
        }
        .b2b-table tbody tr {
          border-bottom: 1px solid #f3f4f6;
        }
        .b2b-table tbody tr:last-child {
          border-bottom: 2px solid #e5e7eb;
        }
        .b2b-table tbody tr:nth-child(even) {
          background: #f9fafb;
        }
        .b2b-table tbody td {
          padding: 2.5mm 3mm;
          font-size: 9.5pt;
          vertical-align: middle;
        }
        .b2b-table tbody td.b2b-num {
          text-align: center;
          font-weight: 600;
        }
        .b2b-table tbody td.b2b-left {
          text-align: left;
          font-weight: 600;
        }

        /* ── Totals ─────────────────────────────────────────────────── */
        .b2b-totals-wrap {
          display: flex;
          justify-content: flex-start;
          margin-top: 5mm;
        }
        .b2b-totals-box {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          overflow: hidden;
          min-width: 68mm;
        }
        .b2b-totals-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 2.5mm 5mm;
          gap: 10mm;
          border-bottom: 1px solid #f3f4f6;
          font-size: 9pt;
        }
        .b2b-totals-row:last-child {
          border-bottom: none;
        }
        .b2b-totals-row.b2b-grand {
          background: #eff6ff;
          font-weight: 700;
          font-size: 10.5pt;
        }
        .b2b-totals-label {
          color: #6b7280;
          white-space: nowrap;
        }
        .b2b-totals-value {
          font-weight: 600;
          white-space: nowrap;
        }
        .b2b-totals-row.b2b-grand .b2b-totals-label {
          color: #1d4ed8;
          font-weight: 700;
        }
        .b2b-totals-row.b2b-grand .b2b-totals-value {
          color: #2563eb;
          font-size: 12pt;
        }

        /* ── Footer ─────────────────────────────────────────────────── */
        .b2b-footer {
          position: absolute;
          bottom: 10mm;
          right: 18mm;
          left: 18mm;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 3mm;
          border-top: 1px solid #e5e7eb;
        }
        .b2b-footer-text {
          font-size: 7.5pt;
          color: #9ca3af;
        }
      `}</style>

      <div className="b2b-order-print" ref={ref}>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="b2b-header">
          {/* Right: logo */}
          <div className="b2b-logo">
            <img src="/logo.svg" alt="Logo" style={{ height: '40px', width: 'auto' }} />
          </div>

          {/* Left: document title */}
          <div style={{ textAlign: 'left' }}>
            <p className="b2b-doc-title">הזמנה</p>
            <p className="b2b-doc-subtitle">#{data.orderId}</p>
          </div>
        </div>

        {/* ── Meta grid ───────────────────────────────────────────────────── */}
        <div className="b2b-meta-grid">

          {/* Supplier name */}
          <div className="b2b-meta-box" style={{ borderColor: '#bfdbfe' }}>
            <div className="b2b-meta-label">ספק</div>
            <div className="b2b-meta-value">{data.supplierName}</div>
          </div>

          {/* Client name */}
          <div className="b2b-meta-box" style={{ borderColor: '#bfdbfe' }}>
            <div className="b2b-meta-label">לקוח</div>
            <div className="b2b-meta-value">{data.clientName || '—'}</div>
          </div>

          {/* Date + Status */}
          <div className="b2b-meta-box">
            <div className="b2b-meta-label">תאריך</div>
            <div className="b2b-meta-value">{fmtDate(data.date)}</div>
            <div className="b2b-status-badge">{data.status}</div>
          </div>
        </div>

        {/* ── Items table ─────────────────────────────────────────────────── */}
        <table className="b2b-table">
          <thead>
            <tr>
              <th style={{ width: '4%' }}>#</th>
              <th style={{ width: '28%' }}>מוצר</th>
              <th style={{ width: '12%' }}>מק&quot;ט</th>
              <th className="b2b-num" style={{ width: '12%' }}>כמות מבוקשת</th>
              <th className="b2b-num" style={{ width: '12%' }}>כמות מאושרת</th>
              <th className="b2b-num" style={{ width: '14%' }}>מחיר יחידה</th>
              {hasDiscount && <th className="b2b-num" style={{ width: '12%' }}>הנחה</th>}
              <th className="b2b-left" style={{ width: '18%' }}>סה״כ שורה</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((line, idx) => {
              const qtyDiffers = line.approved_quantity !== line.requested_quantity;
              return (
                <tr key={idx}>
                  <td style={{ color: '#9ca3af', fontSize: '8pt' }}>{idx + 1}</td>
                  <td style={{ fontWeight: 500 }}>{line.product_name}</td>
                  <td style={{ color: '#6b7280', fontSize: '8.5pt' }}>{line.product_sku}</td>
                  <td className="b2b-num" style={{ color: '#374151' }}>
                    {line.requested_quantity.toLocaleString('he-IL')}
                  </td>
                  <td
                    className="b2b-num"
                    style={
                      qtyDiffers
                        ? { color: '#d97706', fontWeight: 700 }
                        : { color: '#2563eb', fontWeight: 700 }
                    }
                  >
                    {line.approved_quantity.toLocaleString('he-IL')}
                  </td>
                  <td className="b2b-num" style={{ color: '#374151' }}>
                    {fmtCurrency(line.unit_price)}
                  </td>
                  {hasDiscount && (
                    <td className="b2b-num" style={{ color: '#047857' }}>
                      {line.discount ? fmtCurrency(line.discount) : ''}
                    </td>
                  )}
                  <td className="b2b-left">
                    {fmtCurrency(line.total_line_price)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* ── Totals ──────────────────────────────────────────────────────── */}
        <div className="b2b-totals-wrap">
          <div className="b2b-totals-box">
            {hasDiscount && (
              <>
                <div className="b2b-totals-row">
                  <span className="b2b-totals-label">סה״כ לפני הנחה</span>
                  <span className="b2b-totals-value">{fmtCurrency(data.subtotal + (data.discountTotal ?? 0))}</span>
                </div>
                <div className="b2b-totals-row">
                  <span className="b2b-totals-label">הנחה</span>
                  <span className="b2b-totals-value" style={{ color: '#047857' }}>-{fmtCurrency(data.discountTotal ?? 0)}</span>
                </div>
              </>
            )}
            <div className="b2b-totals-row">
              <span className="b2b-totals-label">סכום ביניים</span>
              <span className="b2b-totals-value">{fmtCurrency(data.subtotal)}</span>
            </div>
            {showVat && (
              <div className="b2b-totals-row">
                <span className="b2b-totals-label">מע״מ {data.vatRate}%</span>
                <span className="b2b-totals-value">{fmtCurrency(vatAmount)}</span>
              </div>
            )}
            <div className="b2b-totals-row b2b-grand">
              <span className="b2b-totals-label">סה״כ לתשלום</span>
              <span className="b2b-totals-value">{fmtCurrency(data.grandTotal)}</span>
            </div>
          </div>
        </div>

        {data.discountReason && (
          <p style={{ marginTop: '8px', fontSize: '9pt', color: '#374151' }}>
            סיבת הנחה: <strong>{data.discountReason}</strong>
          </p>
        )}

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <div className="b2b-footer">
          <span className="b2b-footer-text">נוצר אוטומטית</span>
          <span className="b2b-footer-text">
            הופק: {fmtDate(new Date().toISOString())}
          </span>
        </div>

      </div>
    </>
  );
});
